import React from 'react';
import { Select, DatePicker, Input, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { STATUS_ORDER, STATUS_META } from './utils';

const { Option } = Select;
const { RangePicker } = DatePicker;

// Filter-bar shell. Neutralises the app-wide green focus/hover outline on the
// inputs inside the Task Board filter bar (kept a subtle grey instead), without
// touching the global input styling used elsewhere.
const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  padding: 12px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #fff;
  margin-bottom: 16px;

  && .ant-input:hover,
  && .ant-input:focus,
  && .ant-input-focused,
  && .ant-input-affix-wrapper:hover,
  && .ant-input-affix-wrapper:focus,
  && .ant-input-affix-wrapper-focused,
  && .ant-picker:hover,
  && .ant-picker:focus,
  && .ant-picker-focused,
  && .ant-select:not(.ant-select-disabled):hover .ant-select-selector,
  && .ant-select-focused:not(.ant-select-disabled) .ant-select-selector {
    border-color: #d9d9d9 !important;
    box-shadow: none !important;
  }
  /* Range picker's sliding underline between the start/end fields is the
     brand green too — neutralise it. */
  && .ant-picker .ant-picker-active-bar {
    background: #d9d9d9 !important;
  }
`;

const PRIORITY_OPTIONS = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

// The Figma also shows Property and Task Type chips — omitted for now: tasks are
// scoped to the active property and the model has no task-type field (both would
// need a data-source decision). The rest are wired to real task data.
const FilterBar = ({ filters, setFilters, assigneeOptions, tagOptions, onClear }) => {
  const set = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const chip = { minWidth: 140 };

  return (
    <Bar className="taskboard-filter-bar">
      <Select
        allowClear
        showSearch
        optionFilterProp="children"
        placeholder="Assignee"
        style={chip}
        value={filters.assignee || undefined}
        onChange={(v) => set('assignee', v)}
      >
        {assigneeOptions.map((a) => (
          <Option key={a.id} value={String(a.id)}>
            {a.name}
          </Option>
        ))}
      </Select>

      <Select
        allowClear
        placeholder="Priority"
        style={chip}
        value={filters.priority || undefined}
        onChange={(v) => set('priority', v)}
      >
        {PRIORITY_OPTIONS.map((p) => (
          <Option key={p.value} value={p.value}>
            {p.label}
          </Option>
        ))}
      </Select>

      <Select
        allowClear
        placeholder="Status"
        style={chip}
        value={filters.status || undefined}
        onChange={(v) => set('status', v)}
      >
        {STATUS_ORDER.map((s) => (
          <Option key={s} value={s}>
            {STATUS_META[s].label}
          </Option>
        ))}
      </Select>

      <Select
        allowClear
        showSearch
        optionFilterProp="children"
        placeholder="Tag"
        style={chip}
        value={filters.tag || undefined}
        onChange={(v) => set('tag', v)}
      >
        {tagOptions.map((t) => (
          <Option key={t} value={t}>
            {t}
          </Option>
        ))}
      </Select>

      <RangePicker
        style={{ minWidth: 240 }}
        value={filters.dateRange || null}
        onChange={(v) => set('dateRange', v)}
      />

      <Button type="link" onClick={onClear} style={{ paddingLeft: 4 }}>
        Clear filters
      </Button>

      <Input
        allowClear
        prefix={<SearchOutlined style={{ color: '#98a2b3' }} />}
        placeholder="Search tasks..."
        style={{ minWidth: 220, marginLeft: 'auto' }}
        value={filters.search}
        onChange={(e) => set('search', e.target.value)}
      />
    </Bar>
  );
};

export default FilterBar;
